import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetOrderSample } from "../../services/OrderService";
import CommonLoader from "../common/CommonLoader";
import { NotificationManager } from "react-notifications";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { 
  IconButton, 
  Tooltip, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Box,
  Chip,
  Divider,
  Paper
} from "@mui/material";
import {
  Assignment,
  LocationOn,
  Science,
  AttachMoney,
  Flag,
  DateRange,
  Description
} from "@mui/icons-material";

const ViewOrderSample = () => {
  const [loading, setLoading] = useState(false);
  const [sampleData, setSampleData] = useState(null);
  const { orderNumber, sampleNumber } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    getSampleData(orderNumber, sampleNumber);
  }, [orderNumber, sampleNumber]);

  const getSampleData = async (orderNumber, sampleNumber) => {
    try {
      setLoading(true);
      let response = await GetOrderSample(orderNumber, sampleNumber);
      setSampleData(response.results);
    } catch (err) {
      NotificationManager.error("Error getting sample data. " + err, "Error");
    } finally {
      setLoading(false);
    }
  };

  const goToNextSample = () => {
    const nextSampleNumber = parseInt(sampleNumber) + 1;
    navigate(`/lab-orders/${orderNumber}/samples/${nextSampleNumber}`);
  };

  const goToPreviousSample = () => {
    const previousSampleNumber = parseInt(sampleNumber) - 1;
    navigate(`/lab-orders/${orderNumber}/samples/${previousSampleNumber}`);
  };

  const goBackToOrder = () => {
    navigate(`/vlp/lab-orders/${orderNumber}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatBoolean = (value) => {
    if (value === null || value === undefined) return null;
    return value;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `$${value.toFixed(2)}`;
  };

  const InfoRow = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      {icon && <Box sx={{ mr: 1, color: '#1976d2' }}>{icon}</Box>}
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '140px', color: '#555' }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ ml: 1, color: '#333' }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  const StatusChip = ({ label, value, color = "default" }) => {
    if (value === null || value === undefined) return null;
    return (
      <Chip 
        label={`${label}: ${value ? 'Yes' : 'No'}`}
        color={value ? color : "default"}
        variant={value ? "filled" : "outlined"}
        size="small"
        sx={{ mr: 1, mb: 1 }}
      />
    );
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Navigation */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            
            <Tooltip title="Previous Sample">
              <IconButton onClick={goToPreviousSample} sx={{ mr: 1 }}>
                <ArrowBackRoundedIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Next Sample">
              <IconButton onClick={goToNextSample}>
                <ArrowForwardRoundedIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Sample #{sampleNumber}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: '#666' }}>
              from Order #{" "}
              <a
                href={`/vlp/edit/lab-orders/${orderNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 600
                }}
              >
                {orderNumber}
              </a>
            </Typography>
          </Box>
        </Box>
      </Paper>

      {sampleData !== null && (
        <Grid container spacing={3}>
          {/* Basic Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assignment sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Basic Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Lab Number" value={sampleData.labNumber} />
                <InfoRow label="Lab ID" value={sampleData.labId} />
                <InfoRow label="Client Job Number" value={sampleData.clientJobNumber} />
                <InfoRow label="Global Job Number" value={sampleData.globalJobNumber} />
                <InfoRow label="Process ID" value={sampleData.processId} />
                <InfoRow label="Plant ID" value={sampleData.plantId} />
                <InfoRow label="Facility ID" value={sampleData.facilityId} />
                <InfoRow label="Record ID" value={sampleData.recordid} />
              </CardContent>
            </Card>
          </Grid>

          {/* Sample Details Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Science sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Sample Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Sample Matrix" value={sampleData.sampleMatrix} />
                <InfoRow label="Sample Location" value={sampleData.sampleIdentity} />
                <InfoRow label="Sample Description" value={sampleData.sampleDescription} />
                <InfoRow label="Sample Type" value={sampleData.sampleType} />
                <InfoRow label="Samtype" value={sampleData.samtype} />
                <InfoRow label="Priority" value={sampleData.priority} />
                <InfoRow label="Pool Sample Status" value={sampleData.poolSampleStatus} />
                <InfoRow label="Repeat Sample Type" value={sampleData.repeatSampleType} />
              </CardContent>
            </Card>
          </Grid>

{/* Location Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOn sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Location & Physical Properties
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Station" value={sampleData.station} />
                <InfoRow label="Site" value={sampleData.site} />
                <InfoRow label="County" value={sampleData.county} />
                <InfoRow label="Latitude" value={sampleData.latitude} />
                <InfoRow label="Longitude" value={sampleData.longitude} />
                <InfoRow label="Depth" value={sampleData.depth ? `${sampleData.depth} m` : 'N/A'} />
                <InfoRow label="Rep" value={sampleData.rep} />
                <InfoRow label="Color" value={sampleData.color} />
                <InfoRow label="Clarity" value={sampleData.clarity} />
                <InfoRow label="Odor" value={sampleData.odor} />
                <InfoRow label="Sampling Point ID" value={sampleData.samplingPointId} />
              </CardContent>
            </Card>
          </Grid>

          
          {/* Collection Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DateRange sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Collection Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Date Collected" value={formatDate(sampleData.dateCollected)} />
                <InfoRow label="Time Collected" value={sampleData.timeCollected} />
                <InfoRow label="Date Received" value={formatDate(sampleData.dateReceived)} />
                <InfoRow label="Time Received" value={sampleData.timeReceived} />
                <InfoRow label="Collected By" value={sampleData.collectedBy} />
                <InfoRow label="Original Sample Date" value={formatDate(sampleData.originalSampleDate)} />
                <InfoRow label="Date Collected Initial" value={formatDate(sampleData.dateCollectedInitial)} />
              </CardContent>
            </Card>
          </Grid>

          

          {/* Financial Information Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Financial Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <InfoRow label="Quoted Price" value={formatCurrency(sampleData.quotedPrice)} />
                <InfoRow label="Amount Paid" value={formatCurrency(sampleData.amountPaid)} />
                <Box sx={{ mt: 2 }}>
                  <StatusChip label="Billable" value={formatBoolean(sampleData.billable)} color="success" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Status Flags Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 'fit-content' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Flag sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Status Flags
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <StatusChip label="Rush" value={formatBoolean(sampleData.rush)} color="error" />
                  <StatusChip label="Hold" value={formatBoolean(sampleData.hold)} color="warning" />
                  <StatusChip label="Recheck" value={formatBoolean(sampleData.recheck)} color="info" />
                  <StatusChip label="Compliance Sample" value={formatBoolean(sampleData.complianceSample)} color="primary" />
                  <StatusChip label="Confirmation Sample" value={formatBoolean(sampleData.confirmationSample)} color="secondary" />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Additional Information Card */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Description sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Additional Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <InfoRow label="Lab" value={sampleData.otherLabName} />
                    <InfoRow label="Original Lab ID" value={sampleData.originalLabId} />
                    <InfoRow label="Ordered Bottle Record ID" value={sampleData.orderedBottleRecordId} />
                  </Grid>
                </Grid>
                
                {sampleData.comments && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
                      Comments:
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                      <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6 }}>
                        {sampleData.comments}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <CommonLoader loading={loading} />
    </Box>
  );
};

export default ViewOrderSample;